import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import InputRadio from "components/atoms/input/Radio";
import { Caption, H4, H5, TextSm } from "components/atoms/text";
import InputText from "components/atoms/input/Text";
import Btn from "components/atoms/buttons";
import Modal from "components/organisms/Modal";
import FormUser from "components/templates/signUp/Form/User";
import FormCompany from "components/templates/signUp/Form/Company";
import ModalWelcome from "components/templates/signUp/ModalWelcome";
import ModalCheckId from "components/templates/signUp/ModalCheckId";
import ModalWelcomeBeta from "components/templates/signUp/ModalWelcomeBeta";
import COLORS from "components/atoms/colors";
import InputTextLine from "components/atoms/input/TextLine";

import useInput from "hooks/useInput";
import SignUpSuccess from "types/SignUpSuccess";
import REGEXP from "datas/regexp";

import useSignUpUser from "apis/mutations/user/useSignUp";
import useCheckEmailUser from "apis/mutations/user/useCheckEmail";
import useCheckEmailCompany from "apis/mutations/company/useCheckEmail";
import useSignUpCompany from "apis/mutations/company/useSignUp";
import useJob from "apis/queries/useJob";

const initial = {
  userName: "",
  password: "",
  job: "",
  contactNumber: "",
  email: "",
  managerName: "",
  managerNumber: "",
  address: "",
  site: "",
  license: "",
  name: "",
  phoneNumber: "",
  companyName: "",
};

type SignType = "user" | "company";

const Form = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [signUpType, setSignUpType] = useState<SignType>("user");
  const [errorMessage, setErrorMessage] = useState<
    string | string[] | undefined
  >("");
  const [signUpState, dispatch] = useInput(initial);
  const [jobEtc, setJobEtc] = useState("");
  const [isPossibleEmail, setIsPossibleEmail] = useState(false);
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [signUpSuccess, setSignUpSuccess] = useState<SignUpSuccess>();
  const [agree1, setAgree1] = useState(false);
  const [agree2, setAgree2] = useState(false);

  const [userAddress, dispatchUserAddress] = useInput({
    postCode: "",
    baseAddress: "",
    detailAddress: "",
  });

  const [companyAddress, dispatchCompanyAddress] = useInput({
    postCode: "",
    baseAddress: "",
    detailAddress: "",
  });

  //업종 목록
  const jobs = useJob();

  // 아이디 체크 - 일반회원
  const checkEmailUser = useCheckEmailUser();

  // 아이디 체크 - 전문가회원
  const checkEmailCompany = useCheckEmailCompany();

  // 회원가입 api - 일반회원
  const signUpUser = useSignUpUser({
    onError: (message) => setErrorMessage(message),
    onSuccess: (res) => setSignUpSuccess(res),
  });

  //회원가입 api - 전문가회원
  const signUpCompany = useSignUpCompany({
    onError: (message) => setErrorMessage(message),
    onSuccess: (res) => setSignUpSuccess(res),
  });

  const {
    job,
    userName,
    password,
    name,
    phoneNumber,
    email,
    contactNumber,
    managerName,
    managerNumber,
    license,
  } = signUpState;

  //이메일 중복체크
  const handleCheckEmail = (onOpenModal: () => void) => {
    if (!userName) return;

    const mutateOption = {
      onSettled: () => onOpenModal(),
      onSuccess: () => setIsPossibleEmail(true),
      onError: () => setIsPossibleEmail(false),
    };

    if (signUpType === "user") {
      checkEmailUser(userName, mutateOption);
    } else {
      checkEmailCompany(userName, mutateOption);
    }
  };

  // 회원가입 유효성 검사
  const handleVlidation = () => {
    const isEmptyCommon = !job || !userName || !password;
    const isEmptyUser = isEmptyCommon || !name || !phoneNumber || !email;
    const isEmptyCompany =
      isEmptyCommon ||
      !name ||
      !contactNumber ||
      !email ||
      !companyAddress.postCode ||
      !companyAddress.baseAddress ||
      !managerName ||
      !managerNumber ||
      !license;

    if (job === "기타" && !jobEtc) {
      setErrorMessage("*표시는 필수 작성 항목입니다.");
      return false;
    }

    if (signUpType === "user") {
      if (isEmptyUser) {
        setErrorMessage("*표시는 필수 작성 항목입니다.");
        return false;
      }

      if (!REGEXP.number.test(phoneNumber)) {
        setErrorMessage("연락처는 숫자를 입력해주세요.");
        return false;
      }
    }

    if (signUpType === "company") {
      if (isEmptyCompany) {
        setErrorMessage("*표시는 필수 작성 항목입니다.");
        return false;
      }

      if (
        !REGEXP.number.test(contactNumber) ||
        !REGEXP.number.test(managerNumber)
      ) {
        setErrorMessage("연락처는 숫자를 입력해주세요.");
        return false;
      }
    }

    if (!REGEXP.id.test(userName)) {
      setErrorMessage(
        "4~20자리/영문,숫자,특수문자(!@#$%^&*?_~+=(){}.,;:-<>)조합으로 아이디를 작성해주세요."
      );
      return false;
    }

    if (!isPossibleEmail) {
      setErrorMessage("아이디가 없거나 중복되는 지 확인해주세요.");
      return false;
    }

    if (!REGEXP.password.test(password)) {
      setErrorMessage(
        "8자리 이상/대문자,소문자,숫자, 특수문자(!@#$%^&*?_~+=(){}.,;:-<>)중 3 종류 조합으로 비밀번호를 작성해주세요."
      );
      return false;
    }

    if (password !== passwordConfirm) {
      setErrorMessage("비밀번호가 일치하지 않습니다.");
      return false;
    }

    if (!REGEXP.email.test(email)) {
      setErrorMessage("이메일형식이 아닙니다.");
      return false;
    }

    if (!agree1 || !agree2) {
      setErrorMessage("이용약관 및 개인정보수집에 동의해 주세요");
      return false;
    }

    setErrorMessage("");
    return true;
  };

  //회원가입
  const handleSignUp = (onOpenModal: () => void) => {
    if (!handleVlidation()) return;

    const newJob = job === "기타" ? jobEtc : job;

    if (signUpType === "user") {
      //일반회원
      const {
        contactNumber,
        license,
        managerName,
        managerNumber,
        site,
        ...user
      } = signUpState;
      const newAddress = JSON.stringify(userAddress);
      const userData = { ...user, job: newJob, address: newAddress };

      signUpUser(userData, {
        onSuccess: () => onOpenModal(),
      });
    } else {
      //전문가회원
      const { companyName, phoneNumber, ...company } = signUpState;
      const newAddress = JSON.stringify(companyAddress);
      const companyData = { ...company, job: newJob, address: newAddress };

      signUpCompany(companyData, {
        onSuccess: () => onOpenModal(),
      });
    }
  };

  return (
    <form className="w-full">
      <TitleWrap>
        <H4>
          업종<Essential>*</Essential>
        </H4>
        <TitleLine />
      </TitleWrap>
      <ul className="flex gap-10 justify-between mt-5 mb-10 md:flex-col md:gap-5">
        {jobs?.map((j) => {
          const isEtc = j.name === "기타";

          return (
            <li key={j.id} className={`flex md:flex-1`}>
              <InputRadio
                name="job"
                value={j.name}
                onChange={(event) => dispatch({ event })}
                checked={job === j.name}
                children={
                  <>
                    <TextSm className="flex-none">{j.name}</TextSm>
                    {isEtc && (
                      <InputTextLine
                        placeholder="직접 입력"
                        value={jobEtc}
                        onChange={(e) => setJobEtc(e.target.value)}
                        className="md:!w-[80px]"
                      />
                    )}
                  </>
                }
                className="gap-3 md:flex-1"
              />
            </li>
          );
        })}
      </ul>

      <TitleWrap>
        <H4>로그인 정보</H4>
        <TitleLine />
      </TitleWrap>

      <H5 className="mt-5 mb-3">
        아이디<Essential>*</Essential>
      </H5>
      <div className="flex gap-5">
        <InputText
          placeholder="4~20자리/영문,숫자,특수문자 조합"
          className={{ container: "w-[420px] md:w-full" }}
          name="userName"
          value={userName}
          onChange={(event) => dispatch({ event })}
          maxLength={20}
          shape="round"
        />
        <Modal
          activator={(onOpen) => (
            <Btn.Blue
              onClick={() => handleCheckEmail(onOpen)}
              size="large"
              type="button"
              // inActive={!userName}
              radiusFull
              width="100px"
            >
              중복확인
            </Btn.Blue>
          )}
          modal={(onClose) => (
            <ModalCheckId onClose={onClose} isPossibleEmail={isPossibleEmail} />
          )}
        />
      </div>

      <H5 className="mt-5 mb-3">
        비밀번호<Essential>*</Essential>
      </H5>
      <InputText
        placeholder="8자리 이상/대,소문자,숫자, 특수문자 중 3가지 조합"
        className={{ container: "w-[420px] md:w-full" }}
        name="password"
        type="password"
        value={password}
        onChange={(event) => dispatch({ event })}
        shape="round"
      />

      <H5 className="mt-5 mb-3">
        비밀번호 확인<Essential>*</Essential>
      </H5>
      <InputText
        type="password"
        placeholder="작성하신 비밀번호와 동일하게 입력"
        className={{ container: "w-[420px] mt-3 md:w-full" }}
        value={passwordConfirm}
        onChange={(e) => setPasswordConfirm(e.target.value)}
        shape="round"
      />

      <TitleWrap className="mt-10 mb-2">
        <H4>개인정보</H4>
        <TitleLine />
        <div className="flex items-center gap-5">
          <InputRadio
            name="userType"
            value="user"
            labelText="일반"
            checked={signUpType === "user"}
            onChange={(e) => setSignUpType(e.target.value as SignType)}
          />
          <InputRadio
            name="userType"
            value="company"
            labelText="전문가"
            checked={signUpType === "company"}
            onChange={(e) => setSignUpType(e.target.value as SignType)}
          />
        </div>
      </TitleWrap>

      <GuideText>
        *일반회원: AI 마감재 검색 및 스팩북(엑셀파일) 다운로드 가능
        <br />
        *전문가회원: 일반회원 기능 + 실물 샘플 신청 및 단가 열람 가능
      </GuideText>

      {/* 개인정보 섹션 */}
      {signUpType === "user" ? (
        <FormUser
          signUpState={signUpState}
          onDispatch={(event) => dispatch({ event })}
          userAddress={userAddress}
          onDispatchAddress={(option) => dispatchUserAddress({ option })}
        />
      ) : (
        <FormCompany
          signUpState={signUpState}
          onDispatch={dispatch}
          companyAddress={companyAddress}
          onDispatchAddress={(option) => dispatchCompanyAddress({ option })}
        />
      )}

      {errorMessage && (
        <Caption className="text-error-active mt-5 !text-sm !font-bold">
          {errorMessage}
        </Caption>
      )}

      <div className="my-4">
        <TermText>
          <input
            type="checkbox"
            checked={agree1}
            onChange={(e) => setAgree1(e.target.checked)}
          />
          <Link
            to="https://macodi-asset.s3.ap-northeast-2.amazonaws.com/macodi_terms_of_use.pdf"
            target="_blank"
          >
            이용약관(전체보기)
          </Link>
          에 동의합니다.
        </TermText>
        <TermText>
          <input
            type="checkbox"
            checked={agree2}
            onChange={(e) => setAgree2(e.target.checked)}
          />
          <Link
            to="https://macodi-asset.s3.ap-northeast-2.amazonaws.com/macodi_privacy_policy.pdf"
            target="_blank"
          >
            개인정보수집 및 이용(전체보기)
          </Link>
          에 동의합니다.
        </TermText>
      </div>

      <div className="flex justify-center">
        <Modal
          activator={(onOpen) => (
            <div className="md:flex md:justify-end md:w-full md:mt-5">
              <Btn.Blue
                onClick={() => handleSignUp(onOpen)}
                size="large"
                type="button"
                radiusFull
                // width="100px"
              >
                {location.pathname === "/signup"
                  ? "회원가입"
                  : "베타서비스 신청하기"}
              </Btn.Blue>
            </div>
          )}
          modal={(onClose) =>
            location.pathname === "/signup" ? (
              <ModalWelcome
                onClick={() => {
                  navigate("/login");
                  onClose();
                }}
                signUpSuccess={signUpSuccess}
              />
            ) : (
              <ModalWelcomeBeta onClose={onClose} />
            )
          }
        />
      </div>
    </form>
  );
};

const Essential = styled.span`
  vertical-align: top;
  color: ${COLORS.error.active};
  font-size: 16px;
`;

const TitleWrap = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;

  ${H4} {
    flex: none;
  }
`;

const TitleLine = styled.span`
  display: block;
  flex: 1;
  height: 1px;
  background: ${COLORS.black["20%"]};
`;

const TermText = styled.p`
  display: flex;
  align-items: center;
  gap: 6px;
  &,
  a {
    font-size: 14px;
    color: ${COLORS.black["40%"]};
    word-break: keep-all;
  }

  a {
    color: ${COLORS.black["100%"]};
    text-decoration: underline;
  }
`;

const GuideText = styled.p`
  margin-bottom: 20px;
  font-size: 16px;
  word-break: keep-all;
  color: ${COLORS.red};
  font-weight: bold;

  @media screen and (max-width: 410px) {
    font-size: 14px;
  }
`;

export default Form;
